import { useState, useEffect } from "react";
import { useAuth } from "../auth/AuthContext";
import LoadingIndicator from "../components/LoadingIndicator";
import { getPlanDetails } from "../config/subPlans";
import { Helmet } from "react-helmet";
const UserDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [liveVoiceUsagePercentage, setLiveVoiceUsagePercentage] = useState(null);

  useEffect(() => {
    setLoading(true);

    // If user data is available, use it instead of simulated data
    if (user) {
      // Simulate loading (in real app, this would be an API call)
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }, [user]);

  // Listen for live voice usage updates from voice chat sessions
  useEffect(() => {
    const handleVoiceUsageUpdate = (event) => {
      const { usagePercentage } = event.detail;
      console.log('📊 Dashboard received voice usage update:', usagePercentage + '%');
      setLiveVoiceUsagePercentage(usagePercentage);
    };

    window.addEventListener('voiceUsageUpdate', handleVoiceUsageUpdate);
    return () => window.removeEventListener('voiceUsageUpdate', handleVoiceUsageUpdate);
  }, []);

  // Helper function to get usage percentage color
  const getUsageColor = (percentage) => {
    if (percentage < 50) return "bg-green-500";
    if (percentage < 80) return "bg-blue-500";
    return "bg-red-500";
  };

  if (!user || loading) {
    return (
      <div className="w-full h-full overflow-auto">
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
          <LoadingIndicator />
        </div>
      </div>
    );
  }

  // Calculate usage stats from user data
  const messagesUsed = parseInt(user['custom:messagesUsed'] || 0);
  const planDetails = getPlanDetails(user['custom:membership']);
  const messagesLimit = parseInt(user['custom:limit'] || planDetails.messageLimit);
  const usagePercentage = (messagesUsed / messagesLimit) * 100;

  // Voice usage stats. voiceLimit_ms is the source of truth (set on first
  // backend call by realtime_token_generator's lazy-backfill). When it's
  // missing we render a "—" instead of the misleading "0%".
  const voiceLimitMs = parseInt(user['custom:voiceLimit_ms'] || 0);
  const voiceLimitMinutes = voiceLimitMs > 0 ? Math.round(voiceLimitMs / 60000) : null;
  const voiceUsageMs = parseInt(user['custom:voiceUsage_ms'] || 0);
  const voiceMinutesUsed = voiceLimitMs > 0 ? +(voiceUsageMs / 60000).toFixed(1) : 0;
  const storedVoicePercentage = parseFloat(user['custom:voiceUsage_percent'] || 0);
  const voiceUsagePercentage = liveVoiceUsagePercentage !== null ? liveVoiceUsagePercentage : storedVoicePercentage;
  const voiceProvisioned = voiceLimitMs > 0;
  const voiceQuotaExceeded = voiceProvisioned && voiceUsagePercentage >= 100;

  return (
    <>
      <Helmet>
        <title>User Dashboard - limkc.com</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      {/* Following top-down UI architecture */}
      <div className="w-full h-full overflow-auto">
      <div className="px-6 py-6 transition-all duration-300 min-h-screen"
   style={{
    paddingTop: 'calc(var(--navbar-height) + 1.5rem)', // Adds navbar height + existing py-6 (1.5rem)
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)'
  }}>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>{user.name}'s Dashboard</h1>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>Welcome to your personal dashboard</p>
          
          <div className="mt-8 grid grid-cols-1 gap-6">
            {/* Usage Summary Card */}
            <div className="p-6 rounded-lg" 
              style={{ 
                backgroundColor: 'var(--bg-secondary)', 
                boxShadow: 'var(--shadow-box)'
              }}>
              <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Usage Summary</h2>
              <div className="mt-4">
                <div className="flex justify-between items-center mb-2">
                  <span style={{ color: 'var(--text-secondary)' }}>Messages Used</span>
                  <span className="font-semibold text-blue-400">{messagesUsed}&nbsp;/&nbsp;{messagesLimit}</span>
                </div>
                <div className="w-full rounded-full h-2.5 mb-4" style={{ backgroundColor: 'rgba(209, 213, 219, 0.3)' }}>
                  <div 
                    className={`${getUsageColor(usagePercentage)} h-2.5 rounded-full`}
                    style={{ width: `${usagePercentage}%` }}
                  ></div>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span style={{ color: 'var(--text-secondary)' }}>Voice Used</span>
                  <span className="font-semibold text-purple-400">
                    {voiceProvisioned
                      ? `${voiceMinutesUsed} / ${voiceLimitMinutes} min (${Math.round(voiceUsagePercentage)}%)`
                      : '—'}
                  </span>
                </div>
                <div className="w-full rounded-full h-2.5 mb-2" style={{ backgroundColor: 'rgba(209, 213, 219, 0.3)' }}>
                  <div
                    className={`${getUsageColor(voiceUsagePercentage)} h-2.5 rounded-full`}
                    style={{ width: `${voiceProvisioned ? voiceUsagePercentage : 0}%` }}
                  ></div>
                </div>
                {voiceQuotaExceeded && (
                  <div className="mb-4 text-xs" style={{ color: 'var(--text-secondary)' }}>
                    Daily quota used. Resets at 1:00 AM UTC.
                  </div>
                )}
                {!voiceQuotaExceeded && <div className="mb-2"></div>}
                <div className="flex justify-between items-center mb-3">
                  <span style={{ color: 'var(--text-secondary)' }}>Membership</span>
                  <span className="font-semibold text-green-400">{planDetails.name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span style={{ color: 'var(--text-secondary)' }}>Next Reset</span>
                  <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{user['custom:resetDate']}</span>
                </div>
                <div className="flex justify-between items-center mt-3">
                  <span style={{ color: 'var(--text-secondary)' }}>Status</span>
                  <span className="font-semibold capitalize" style={{ color: 'var(--text-primary)' }}>{user['custom:accountStatus'] || 'active'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserDashboard;